import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { ChevronUpIcon } from "@jti/brand-icons/chevron-up";
import { setCookie } from "@jti/fe-common";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { useViewport } from "../../hooks/useViewport";
import { useConfig } from "../../providers/useConfig";
import { LanguageSelectorPopupDesktop } from "./components/LanguageSelectorPopupDesktop";
import { LanguageSelectorPopupMobile } from "./components/LanguageSelectorPopupMobile";
import { LanguageSelectorPopupMobileFullscreen } from "./components/LanguageSelectorPopupMobileFullscreen";
import { LanguageSelectorButton } from "./components/LanguageSelectorPopupMobileFullscreen/components/LanguageSelectorButton";
import styles from "./LanguageSelector.module.scss";
const MOBILE_WIDTH = 768;
export const calculateExpireDate = () => {
    const oneYearInMs = 365 * 24 * 60 * 60 * 1000;
    const oneYearAhead = new Date(Date.now() + oneYearInMs);
    return oneYearAhead;
};
export const setLanguageCookie = (languageCode) => {
    setCookie("language", languageCode, "/", calculateExpireDate().toUTCString());
};
export const LanguageSelector = ({ languages, variant, popupContext, customName = "", customClassName = "", }) => {
    const [isOpened, setIsOpened] = useState(false);
    const containerRef = useRef(null);
    const viewport = useViewport();
    const currentLanguage = useMemo(() => languages.find(({ current }) => current), [languages]);
    const { isMobileFullscreenLanguageSelectorEnabled } = useConfig();
    const [isInsideHeader, setIsInsideHeader] = useState(false);
    useEffect(() => {
        if (containerRef.current) {
            const isInside = !!containerRef.current.closest("header");
            setIsInsideHeader(isInside);
        }
    }, []);
    useOutsideClick(containerRef, () => {
        if (viewport.width >= MOBILE_WIDTH) {
            setIsOpened(false);
        }
    });
    const showLanguageSelectorButton = viewport.width < MOBILE_WIDTH && isInsideHeader && isMobileFullscreenLanguageSelectorEnabled;
    const handleChange = ({ associatedCookieValue, path }) => {
        setLanguageCookie(associatedCookieValue);
        window.location.assign(path);
    };
    const renderLanguageName = () => {
        if (variant === "FULL_LANGUAGE_NAME") {
            return currentLanguage?.languageDisplayName;
        }
        if (variant === "CUSTOM_NAME") {
            return customName;
        }
        return currentLanguage?.associatedCookieValue;
    };
    const toggleDropdown = () => {
        setIsOpened((isOpened) => !isOpened);
    };
    const closePopup = () => {
        setIsOpened(false);
    };
    useEffect(() => {
        if (currentLanguage && location.search?.includes("redirect")) {
            setLanguageCookie(currentLanguage.associatedCookieValue);
        }
    }, [currentLanguage, languages]);
    if (languages.length < 2) {
        return null;
    }
    const mobilePopup = isMobileFullscreenLanguageSelectorEnabled ? (_jsx(LanguageSelectorPopupMobileFullscreen, { isOpened: isOpened, onApplyLanguage: handleChange, onClose: closePopup, languages: languages, popupContext: popupContext })) : (_jsx(LanguageSelectorPopupMobile, { isOpened: isOpened, onApplyLanguage: handleChange, onClose: closePopup, languages: languages, popupContext: popupContext }));
    return (_jsxs("div", { "data-testid": "languageSelectorContainer", ref: containerRef, className: classNames(styles.languageSelector, customClassName, {
            [styles.languageSelectorOpened]: isOpened,
        }), children: [showLanguageSelectorButton ? (_jsx(LanguageSelectorButton, { toggleDropdown: toggleDropdown, languageName: renderLanguageName() })) : (_jsxs("button", { onClick: toggleDropdown, "data-testid": "languageSelectorDropdown", type: "button", className: styles.languageSelectorDropdown, children: [_jsx("span", { "data-testid": "languageSelectorDropdownText", children: renderLanguageName() }), isOpened ? (_jsx(ChevronUpIcon, { width: 10, height: 10, className: styles.languageSelectorDropdownArrow })) : (_jsx(ChevronDownIcon, { width: 10, height: 10, className: styles.languageSelectorDropdownArrow }))] })), viewport.width >= MOBILE_WIDTH ? (_jsx(LanguageSelectorPopupDesktop, { isOpened: isOpened, onSelectLanguage: handleChange, languages: languages })) : (mobilePopup)] }));
};
//# sourceMappingURL=LanguageSelector.js.map